import { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
    getEAOpenCampaigns,
    getPlusExclusiveCampaign,
    getTabs,
} from '@client/redux/modules/mylounge';
import { getSemanticArticlesCarouselData } from '@client/redux/modules/semanticArticles';
import type { Campaign } from '@shared/types/mylounge';
import { usePlusExclusiveCampaignPosition } from '@client/components/mylounge/plus/use-plus-exclusive-campaign-position';
import { useContinueShopping } from '@client/components/continue-shopping/hooks/use-continue-shopping';
import { useNewDealsToday } from '@client/components/mylounge/new-deals-today';
import type { GridLayoutContextValue } from './GridLayoutContext';
import { deriveLayoutConfig } from '../utils/deriveLayoutConfig';

import { createCompleteCampaignList } from '../helpers';

import { useTopPicks4UGenderData, type TopPicks4UGenderData } from '../hooks';

interface UseGridLayoutContextValueProps {
    campaigns: Campaign[];
    isPlusEnabled: boolean;
    columnCount: number;
}

export const useGridLayoutContextValue = ({
    campaigns,
    isPlusEnabled,
    columnCount,
}: UseGridLayoutContextValueProps): GridLayoutContextValue => {
    // Fetch data sources
    const { isNewDealsToday } = useNewDealsToday();
    const { articles: semanticArticles } = useSelector(getSemanticArticlesCarouselData);
    const isSemanticArticleCarousel = (semanticArticles?.length ?? 0) > 0;
    const topPicks4UGenderData = useTopPicks4UGenderData();
    const { shouldShowContinueShopping } = useContinueShopping();
    // Merge campaigns with early access
    const earlyAccessOpenCampaignsRaw = useSelector(getEAOpenCampaigns);
    const exclusiveCampaign = useSelector(getPlusExclusiveCampaign);
    const tabsRaw = useSelector(getTabs);

    // to prevent to pass null/undefined values around.
    // In future we should refactor campaigns selector to be the UNIQUE source of truth
    // without filtering and additions
    const earlyAccessOpenCampaigns = useMemo(
        () => earlyAccessOpenCampaignsRaw ?? [],
        [earlyAccessOpenCampaignsRaw],
    );
    // to prevent to pass null/undefined values around
    const tabs = useMemo(() => tabsRaw ?? [], [tabsRaw]);

    // Memoize EA campaign logic to prevent cascading recalculations
    const earlyAccessCampaignLogic = useMemo(() => {
        const hasPlusOffersTab = tabs.some(tab => tab.event === 'PLUS_OFFER_EVENT');
        return {
            hasPlusOffersTab,
            numberOfEACampaignsShouldShown:
                isPlusEnabled && !hasPlusOffersTab ? earlyAccessOpenCampaigns.length : 0,
        };
    }, [tabs, isPlusEnabled, earlyAccessOpenCampaigns]);

    const plusExclusiveCampaignPosition = usePlusExclusiveCampaignPosition({
        columnCount,
        offset: earlyAccessCampaignLogic.numberOfEACampaignsShouldShown,
    });

    const completeCampaignsList = useMemo(
        () =>
            createCompleteCampaignList(
                campaigns,
                earlyAccessOpenCampaigns,
                exclusiveCampaign,
                plusExclusiveCampaignPosition,
            ),
        [campaigns, earlyAccessOpenCampaigns, exclusiveCampaign, plusExclusiveCampaignPosition],
    );

    const campaignIndexRef = useRef(0);

    // Reset index whenever campaign list or layout changes
    useEffect(() => {
        campaignIndexRef.current = 0;
    }, [completeCampaignsList, columnCount]);

    /**
     * Internal method to reset campaign index (called by DeclarativeGrid at render start).
     * Not exposed in context - DeclarativeGrid has direct access via hook.
     */
    const resetCampaignIndex = () => {
        campaignIndexRef.current = 0;
    };

    /**
     * Internal method to get and increment campaign index (called during child traversal).
     * Not exposed in context - only used internally for offset calculation.
     */
    const getAndIncrementIndex = (count: number = 1): number => {
        const currentIndex = campaignIndexRef.current;
        campaignIndexRef.current += count;
        return currentIndex;
    };

    /**
     * Create context value with memoization to prevent unnecessary context invalidations.
     *
     * IMPORTANT FOR CONSUMERS:
     * - Depend on individual fields (campaigns, isPlusEnabled) not the context object itself
     * - ❌ DON'T: useEffect(() => {...}, [ctx]) - will re-run on isPlusEnabled change
     * - ✅ DO:    useEffect(() => {...}, [ctx.isPlusEnabled]) - stable, only when isPlusEnabled changes
     *
     * Context object itself changes when: campaigns list, isPlusEnabled, columnCount, isNewDealsToday, or isSemanticArticleCarousel changes
     */
    const layoutConfig = useMemo(
        () => deriveLayoutConfig(isNewDealsToday, isSemanticArticleCarousel),
        [isNewDealsToday, isSemanticArticleCarousel],
    );

    const contextValue = useMemo(
        () => ({
            campaigns: completeCampaignsList,
            isPlusEnabled,
            columnCount,
            layoutConfig,
            isNewDealsToday,
            isSemanticArticleCarousel,
            topPicks4UGenderData,
            isContinueShopping: shouldShowContinueShopping,
        }),
        [
            completeCampaignsList,
            isPlusEnabled,
            columnCount,
            isNewDealsToday,
            isSemanticArticleCarousel,
            layoutConfig,
            topPicks4UGenderData,
            shouldShowContinueShopping,
        ],
    );

    /**
     * Create extended context value with internal methods for offset calculation.
     * Internal methods are prefixed with __ to indicate private implementation.
     */
    return {
        ...contextValue,
        _resetCampaignIndex: resetCampaignIndex,
        _getAndIncrementIndex: getAndIncrementIndex,
    };
};
